import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Paper,
  Button,
  Breadcrumbs,
  Typography,
  TextField,
  Container,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Pagination,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { getName } from "../Other/common";
import { toast } from "react-toastify";
import { useTheme } from "@emotion/react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import { BASE_URL } from "../../urlConfig/Url";

const CompanyMaster = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [cid, setCID] = useState("");
  const [erpCompCode, setERPCompCode] = useState("");
  const [erpCompCodeError, setERPCompCodeError] = useState(false);
  const [compCode, setCompCode] = useState("");
  const [compCodeError, setCompCodeError] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyNameError, setCompanyNameError] = useState(false);
  const [address, setAddress] = useState("");
  const [addressError, setAddressError] = useState(false);
  const [city, setCity] = useState("");
  const [cityError, setCityError] = useState(false);
  const [state, setState] = useState("");
  const [stateError, setStateError] = useState(false);
  const [status, setStatus] = useState("");
  const [statusError, setStatusError] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formMode, setFormMode] = useState("initial");
  const rowsPerPage = 5;

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.post(
        BASE_URL + "/api/master/getAllCompanies"
      );
      if (response.data.success) {
        setCompanies(response.data.response);
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
    }
  };

  const validateForm = () => {
    let isValid = true;

    if (compCode.trim() === "") {
      setCompCodeError(true);
      isValid = false;
    } else {
      setCompCodeError(false);
    }

    if (erpCompCode.trim() === "") {
      setERPCompCodeError(true);
      isValid = false;
    } else {
      setERPCompCodeError(false);
    }

    if (companyName.trim() === "") {
      setCompanyNameError(true);
      isValid = false;
    } else {
      setCompanyNameError(false);
    }

    if (address.trim() === "") {
      setAddressError(true);
      isValid = false;
    } else {
      setAddressError(false);
    }

    if (city.trim() === "") {
      setCityError(true);
      isValid = false;
    } else {
      setCityError(false);
    }

    if (state.trim() === "") {
      setStateError(true);
      isValid = false;
    } else {
      setStateError(false);
    }

    if (status === "") {
      setStatusError(true);
      isValid = false;
    } else {
      setStatusError(false);
    }

    return isValid;
  };

  const onSubmit = async () => {
    if (validateForm()) {
      // Proceed with form submission
      const data = {
        CCode: compCode,
        ERPComC: erpCompCode,
        CName: companyName,
        Address: address,
        City: city,
        State: state,
        Status: status,
        Barcode: compCode,
        Createdby: getName().Name,
      };

      try {
        const response = await axios.post(
          `${BASE_URL}/api/master/insertCompany`,
          data
        );

        if (response.data.success) {
          toast.success(response.data.message);
          fetchCompanies();
          resetForm();
          setFormMode("initial");
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        console.error("Error submitting company data:", error);
        toast.error("An error occurred while adding the company.");
      }
    } else {
      toast.error("Please fill in all required fields.");
    }
  };

  const onUpdate = async () => {
    const data = {
      CID: cid,
      CompCode: compCode,
      ERPComC: erpCompCode,
      CompanyName: companyName,
      Address: address,
      City: city,
      State: state,
      Status: status,
      // Barcode: compCode,
      Updatedby: getName().Name,
    };
    // console.log(data);
    try {
      const response = await axios.post(
        BASE_URL + "/api/master/updateCompany", // Update API endpoint
        data
      );

      if (response.data.success) {
        toast.success(response.data.message);
        fetchCompanies();
        resetForm(); // Refresh the company list and reset form
        setFormMode("initial");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error updating company data:", error);
      toast.error("An error occurred while updating the company.");
    }
  };

  const resetForm = () => {
    setCompCode("");
    setERPCompCode("");
    setCompanyName("");
    setAddress("");
    setCity("");
    setState("");
    setStatus("");
    setSelectedCompany(null);
    // setFormMode("add");
  };

  const handleAddNew = () => {
    setFormMode("add");
    resetForm();
  };

  const handleCancel = () => {
    resetForm();
    setFormMode("initial");
    stopErrorHelper();
  };

  // Filter data based on search term
  useEffect(() => {
    const searchWords = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    const result = companies.filter((company) =>
      searchWords.every((word) =>
        Object.values(company).some((value) =>
          value.toString().toLowerCase().includes(word)
        )
      )
    );
    setFilteredRows(result);
  }, [searchTerm, companies]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page on search
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const stopErrorHelper=()=>{
    setCompCodeError(false);
    setERPCompCodeError(false);
    setCompanyNameError(false);
    setAddressError(false);
    setCityError(false);
    setStateError(false);
    setStatusError(false);
  }

  const handleRowSelect = (company) => {
    setCID(company.CID);
    setCompCode(company.CompCode);
    setERPCompCode(company.ERPCompCode);
    setCompanyName(company.CompanyName);
    setAddress(company.Address);
    setCity(company.City);
    setState(company.State);
    setStatus(company.Status);
    setSelectedCompany(company);
    setFormMode("edit");
    stopErrorHelper();
  };

  const paginatedRows = filteredRows.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 1, mb: 2 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            to="/dashboard/dashbordhome"
            style={{
              textDecoration: "none",
              color: theme.palette.primary.main,
            }}
          >
            Home
          </Link>
          <Typography color="textPrimary">Company Master</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ marginTop: "-3" }}>
        <Paper elevation={3} sx={{ padding: "2%" }}>
          <Typography variant="h6" mb={2} sx={{ textAlign: "left" }}>
            Company Master(* Fields Are Mandatory)
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Company Code (*)"
                name="Company Code"
                fullWidth
                size="small"
                value={compCode}
                error={compCodeError}
                helperText={compCodeError ? "Company Code is required" : ""}
                onChange={(e) => setCompCode(e.target.value)}
                disabled={formMode === "initial" || formMode === "edit"} // Disable field if in view mode
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="SAP/ERP Company Code (*)"
                name="SAP/ERP Company Code"
                fullWidth
                size="small"
                value={erpCompCode}
                error={erpCompCodeError}
                helperText={erpCompCodeError ? "ERP Company Code is required" : ""}
                onChange={(e) => setERPCompCode(e.target.value)}
                disabled={formMode === "initial" || formMode === "edit"}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Company Name (*)"
                name="Company Name"
                fullWidth
                size="small"
                value={companyName}
                error={companyNameError}
                helperText={companyNameError ? "Company Name is required" : ""}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={formMode === "initial"}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Company Address (*)"
                name="Address"
                fullWidth
                size="small"
                value={address}
                error={addressError}
                helperText={addressError ? "Address is required" : ""}
                onChange={(e) => setAddress(e.target.value)}
                disabled={formMode === "initial"}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="City (*)"
                name="City"
                fullWidth
                size="small"
                value={city}
                error={cityError}
                helperText={cityError ? "City is required" : ""}
                onChange={(e) => setCity(e.target.value)}
                disabled={formMode === "initial"}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="State (*)"
                name="State"
                fullWidth
                size="small"
                value={state}
                error={stateError}
                helperText={stateError ? "State is required" : ""}
                onChange={(e) => setState(e.target.value)}
                disabled={formMode === "initial"}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl
                fullWidth
                size="small"
                disabled={formMode === "initial"}
                error={stateError}
              >
                <InputLabel>Status (*)</InputLabel>
                <Select
                  name="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Deactive">Deactive</MenuItem>
                </Select>
                {statusError &&''}
              </FormControl>
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end" mt={5} spacing={1}>
            {formMode === "initial" && (
              <Grid item>
                <Button variant="contained" size="small" onClick={handleAddNew}>
                  <AddIcon />
                  Add New
                </Button>
              </Grid>
            )}

            {formMode === "add" && (
              <>
                <Grid item>
                  <Button variant="contained" size="small" onClick={onSubmit}>
                    <AddIcon />
                    Submit
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCancel}
                  >
                    <CloseIcon />
                    Cancel
                  </Button>
                </Grid>
              </>
            )}

            {formMode === "edit" && (
              <>
                <Grid item>
                  <Button variant="contained" size="small" onClick={onUpdate}>
                    <ChangeCircleIcon />
                    Update
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCancel}
                  >
                    <CloseIcon />
                    Cancel
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Paper elevation={3} sx={{ padding: "2%" }}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ width: 250 }}
              InputProps={{
                endAdornment: (
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>

          <div className="table-container">
            <table className="responsive-table" style={{ textAlign: "center" }}>
              <thead>
                <tr>
                  <th>Select</th>
                  {/* <th>ERP Code</th> */}
                  <th>Company Code</th>
                  <th>Company Name</th>
                  <th>Address</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Created On</th>
                  <th>Updated By</th>
                  <th>Updated On</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((company) => (
                  <tr key={company.CID}>
                    <td data-label="Select">
                      <button onClick={() => handleRowSelect(company)}>
                        Select
                      </button>
                    </td>
                    {/* <td data-label="ERP Code">{company.ERPCompCode}</td> */}
                    <td data-label="Company Code">{company.CompCode}</td>
                    <td data-label="Company Name">{company.CompanyName}</td>
                    <td data-label="Address">{company.Address}</td>
                    <td data-label="City">{company.City}</td>
                    <td data-label="State">{company.State}</td>
                    <td data-label="Status">{company.Status}</td>
                    <td data-label="Created By">{company.Createdby}</td>
                    <td data-label="Created On">
                      {new Date(company.CreatedOn).toLocaleString()}
                    </td>
                    <td data-label="Updated By">{company.Updatedby}</td>
                    <td data-label="Updated On">
                      {company.UpdatedOn
                        ? new Date(company.UpdatedOn).toLocaleString()
                        : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Pagination
              count={Math.ceil(filteredRows.length / rowsPerPage)}
              page={page}
              onChange={handleChangePage}
              size="small"
            />
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default CompanyMaster;
